import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getPartnerWhatsappSummary } from "../../api/partnerPortalApi";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import PartnerWhatsappFunnel from "../../components/partner/whatsapp/PartnerWhatsappFunnel";
import PartnerWhatsappKpiCard from "../../components/partner/whatsapp/PartnerWhatsappKpiCard";
import PartnerWhatsappNeedsAttention from "../../components/partner/whatsapp/PartnerWhatsappNeedsAttention";
import PartnerWhatsappExportButton from "../../components/partner/whatsapp/PartnerWhatsappExportButton";
import {
  PARTNER_WHATSAPP_NEXT_ACTIONS,
  PARTNER_WHATSAPP_RANGE_OPTIONS,
  buildPartnerWhatsappConversationHref,
} from "../../constants/partnerWhatsappMonitoringConstants";
import {
  formatPartnerWhatsappCount,
  formatPartnerWhatsappRate,
  isPartnerWhatsappSummaryEmpty,
} from "../../utils/partnerWhatsappSummaryViewModel";
import { exportPartnerWhatsappSummaryCsv } from "../../utils/partnerWhatsappExport";
import "../../styles/partnerPortalDashboard.css";

function WhatsappMonitoringSkeleton() {
  return (
    <div className="partner-dashboard partner-dashboard-skeleton" aria-busy="true">
      <div className="partner-skeleton-block partner-skeleton-hero" />
      <div className="partner-kpi-grid">
        {[1, 2, 3, 4, 5, 6].map((id) => (
          <div key={id} className="partner-skeleton-block partner-skeleton-kpi" />
        ))}
      </div>
    </div>
  );
}

export default function PartnerWhatsappMonitoringPage() {
  const { path, portalLabel } = usePartnerPortalBase();
  const conversationsPath = path("whatsapp/conversations");
  const dealerHealthPath = path("whatsapp/dealers");
  const [searchParams, setSearchParams] = useSearchParams();
  const range = searchParams.get("range") === "30d" ? "30d" : "7d";
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getPartnerWhatsappSummary(
        { range },
        { conversationsPath, dealerHealthPath }
      );
      setSummary(body);
    } catch {
      setSummary(null);
      setErrorMessage("We could not load WhatsApp monitoring right now.");
    } finally {
      setLoading(false);
    }
  }, [range, conversationsPath, dealerHealthPath]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const onRangeChange = (event) => {
    const nextRange = event.target.value === "30d" ? "30d" : "7d";
    const nextParams = new URLSearchParams(searchParams);
    if (nextRange === "7d") {
      nextParams.delete("range");
    } else {
      nextParams.set("range", nextRange);
    }
    setSearchParams(nextParams, { replace: true });
  };

  const isEmpty = useMemo(
    () => summary && isPartnerWhatsappSummaryEmpty(summary),
    [summary]
  );

  if (loading) {
    return <WhatsappMonitoringSkeleton />;
  }

  if (errorMessage) {
    return (
      <div className="partner-panel partner-whatsapp-page">
        <header className="partner-dashboard-hero">
          <div>
            <h2>WhatsApp Leads</h2>
            <p className="partner-dashboard-hero__intro">
              Monitor brokered WhatsApp lead flow for your dealers.
            </p>
          </div>
        </header>
        <div className="partner-panel">
          <p className="admin-data-health-error">{errorMessage}</p>
        </div>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  const totals = summary.totals || {};
  const rates = summary.rates || {};

  return (
    <div className="partner-dashboard partner-whatsapp-page">
      <header className="partner-dashboard-hero">
        <div>
          <p className="partner-dashboard-hero__eyebrow">{portalLabel}</p>
          <h2>WhatsApp Leads</h2>
          <p className="partner-dashboard-hero__intro">
            Monitor brokered WhatsApp lead flow for your dealers.
          </p>
        </div>
        <div className="partner-whatsapp-controls">
          <Link className="partner-dashboard-btn partner-dashboard-btn--primary" to={conversationsPath}>
            View conversations
          </Link>
          <Link className="partner-dashboard-btn" to={dealerHealthPath}>
            Dealer health
          </Link>
          <PartnerWhatsappExportButton
            label="Export summary CSV"
            onExport={() => exportPartnerWhatsappSummaryCsv({ range })}
          />
          <label className="partner-whatsapp-controls__range">
            Range
            <select value={range} onChange={onRangeChange} aria-label="Summary range">
              {PARTNER_WHATSAPP_RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="partner-dashboard-btn" onClick={loadSummary}>
            Refresh
          </button>
        </div>
      </header>

      {isEmpty ? (
        <section className="partner-panel partner-whatsapp-empty">
          <h3>No brokered WhatsApp leads yet.</h3>
          <p className="muted">
            When buyers submit WhatsApp leads for your dealers, activity will appear here.
          </p>
        </section>
      ) : null}

      <div className="partner-kpi-grid">
        <PartnerWhatsappKpiCard
          icon="📲"
          label="Brokered leads"
          value={formatPartnerWhatsappCount(totals.brokeredLeads)}
          hint={`${summary.dealerScopeCount || 0} dealer(s) in scope`}
          tone="blue"
        />
        <PartnerWhatsappKpiCard
          icon="✅"
          label="Buyer confirmation rate"
          value={formatPartnerWhatsappRate(rates.buyerConfirmationRate)}
          hint={`${formatPartnerWhatsappCount(totals.buyerConfirmed)} confirmed`}
          tone="green"
        />
        <PartnerWhatsappKpiCard
          icon="🏬"
          label="Dealer accept rate"
          value={formatPartnerWhatsappRate(rates.dealerAcceptRate)}
          hint={`${formatPartnerWhatsappCount(totals.dealerAccepted)} accepted`}
          tone="violet"
        />
        <PartnerWhatsappKpiCard
          icon="🤝"
          label="Handoffs completed"
          value={formatPartnerWhatsappCount(totals.handoffsCompleted)}
          hint={formatPartnerWhatsappRate(rates.handoffCompletionRate) + " completion rate"}
          tone="green"
        />
        <PartnerWhatsappKpiCard
          icon="📞"
          label="Manual outreach required"
          value={formatPartnerWhatsappCount(totals.manualOutreachRequired)}
          hint="Needs partner or dealer follow-up"
          tone="amber"
        />
        <PartnerWhatsappKpiCard
          icon="⚠️"
          label="Failed sends"
          value={formatPartnerWhatsappCount(totals.failedSends)}
          hint="Broker submission or template failures"
          tone="amber"
        />
      </div>

      <div className="partner-dashboard-grid">
        <section className="partner-panel">
          <div className="partner-panel__header">
            <h3>Broker funnel</h3>
            <span className="muted">{range === "30d" ? "Last 30 days" : "Last 7 days"}</span>
          </div>
          <PartnerWhatsappFunnel funnel={summary.funnel} />
        </section>

        <section className="partner-panel">
          <div className="partner-panel__header">
            <h3>Needs attention</h3>
          </div>
          <PartnerWhatsappNeedsAttention
            items={summary.needsAttention}
            emptyMessage="No urgent WhatsApp broker signals in this range."
          />
        </section>
      </div>

      <section className="partner-panel partner-whatsapp-next-actions">
        <div className="partner-panel__header">
          <h3>Next actions</h3>
        </div>
        <ul className="partner-whatsapp-next-actions__list">
          {PARTNER_WHATSAPP_NEXT_ACTIONS.map((action) => (
            <li key={action.id}>
              {action.filter ? (
                <Link
                  to={buildPartnerWhatsappConversationHref(conversationsPath, action.filter)}
                >
                  {action.label}
                </Link>
              ) : action.hrefSegment ? (
                <Link to={path(action.hrefSegment)}>{action.label}</Link>
              ) : action.note ? (
                <>
                  <strong>{action.label}</strong>
                  <span className="muted">{action.note}</span>
                </>
              ) : (
                <strong>{action.label}</strong>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
